import { Box, Flex, PseudoBox } from '@chakra-ui/core'
import { css, keyframes } from '@emotion/core'
import { ConversationModal } from 'components/GPTConversation/ConversationModal'
import { useConversationModal } from 'hooks/useConversationModal'
import colors from 'constants/colors'
import { Phone } from 'lucide-react'
import React, { useState } from 'react'
import { IPop, RootState } from 'constants/interfaces'
import { PopType } from '../utils'
import { useSelector } from 'react-redux'

// Green phone: wiggle rotation mimicking a ringing phone
const phoneRingKeyframes = keyframes`
  0%, 50%, 100% { transform: rotate(0deg); }
  5%            { transform: rotate(20deg); }
  10%           { transform: rotate(-20deg); }
  15%           { transform: rotate(20deg); }
  20%           { transform: rotate(-20deg); }
  25%           { transform: rotate(12deg); }
  30%           { transform: rotate(-8deg); }
  35%           { transform: rotate(0deg); }
`

// Green circle: expand + fade glow ring
const phonePulseKeyframes = keyframes`
  0%   { box-shadow: 0 0 0 0   rgba(45, 206, 133, 0.55); }
  60%  { box-shadow: 0 0 0 18px rgba(45, 206, 133, 0); }
  100% { box-shadow: 0 0 0 0   rgba(45, 206, 133, 0); }
`

const ringIconCss = css`
  animation: ${phoneRingKeyframes} 2s ease-in-out infinite;
  display: inline-flex;
`

const pulseCss = css`
  animation: ${phonePulseKeyframes} 2s ease-in-out infinite;
`

export const boxScaleEffect = css`
  &:hover {
    transform: scale(1.1);
  }
  transition: transform 0.3s ease, margin 0.3s ease;
  cursor: pointer;
`

interface IButtonProps {
  icon: React.ReactNode
  color: string
  onClick: () => void
  editMode?: boolean
  isRinging?: boolean
}

const PhoneButton = ({ icon, color, onClick, editMode, isRinging }: IButtonProps) => (
  <PseudoBox
    backgroundColor={color}
    borderRadius="50%"
    w="74px"
    h="74px"
    display="flex"
    alignItems="center"
    justifyContent="center"
    flexShrink={0}
    onClick={onClick}
    color="white"
    pointerEvents={editMode ? 'none' : 'auto'}
    css={[boxScaleEffect, isRinging ? pulseCss : undefined]}
    opacity={editMode ? 0.5 : 1}
    cursor={editMode ? 'not-allowed' : 'pointer'}
  >
    <Box css={isRinging ? ringIconCss : undefined} display="flex" alignItems="center" justifyContent="center">
      {icon}
    </Box>
  </PseudoBox>
)

interface IProps {
  pop: IPop
  handleClose?: (popType: number | undefined) => void
  editMode?: boolean
}

export const StartConversationsBlock = ({ pop, handleClose, editMode = false }: IProps) => {
  const { activeGroupID } = useSelector((state: RootState) => state.config)
  const [isPlaying, setIsPlaying] = useState<boolean>(pop.is_voice_mode ?? false)
  const {
    isOpen,
    openModal,
    closeModal,
    conversation,
    isBotResponseInProgress,
    isWebSocketReady,
    handleSendMessage,
    clearHistory,
    handleReconnect,
    hasWebSocketError,
    isSessionExpired,
    reloadSession,
    stopVoicePlayback,
  } = useConversationModal({
    onError: () => {
      console.error('WebSocket error occurred')
    },
    onReInitialize: () => {
      console.log('WebSocket reinitialized due to timeout')
    },
    prompt_id: pop.prompt_id ?? '',
    isInjectReports: pop.is_inj_reports,
    group_id: activeGroupID!,
    isInstantConversation: false,
    mode: pop.is_voice_mode ? 1 : 0,
    play: isPlaying,
    promptInjections: pop.prompt_injections,
  })

  const handleStartConversations = () => {
    openModal()
  }

  const handleDeclinePop = () => {
    closeModal()
    handleClose?.(PopType.DEFAULT_POP)
  }

  return (
    <>
      <Flex
        alignItems="center"
        dir="ltr"
        justifyContent="space-around"
        w="100%"
        p="16px"
        style={{ overflow: 'visible' }}
      >
        <Box position="relative" zIndex={1} style={{ overflow: 'visible' }}>
          <PhoneButton
            icon={<Phone size={42} />}
            color={colors.greenLight3}
            onClick={handleStartConversations}
            editMode={editMode}
            isRinging
          />
        </Box>
        <PhoneButton
          icon={
            <Box as="span" display="inline-flex" style={{ transform: 'rotate(135deg)' }}>
              <Phone size={42} />
            </Box>
          }
          color={colors.red3}
          onClick={handleDeclinePop}
          editMode={editMode}
        />
      </Flex>
      <ConversationModal
        isOpen={isOpen}
        onClose={closeModal}
        conversation={conversation}
        isBotResponseInProgress={isBotResponseInProgress}
        isWebSocketReady={isWebSocketReady}
        onSendMessage={handleSendMessage}
        onClearHistory={clearHistory}
        onReconnect={handleReconnect}
        hasWebSocketError={hasWebSocketError}
        pop={pop}
        isSessionExpired={isSessionExpired}
        onReloadSession={reloadSession}
        setIsPlaying={setIsPlaying}
        isPlaying={isPlaying}
        onStopVoicePlayback={stopVoicePlayback}
      />
    </>
  )
}
